import isEmpty from 'lodash/isEmpty';
import { AnyAction } from '@reduxjs/toolkit';
import base64 from 'react-native-base64';
import { RootState, Thunk } from 'store/types';
import { Dispatch } from 'redux';
import { DFDHeaders } from 'serverTypes/authentication';
import {
  visitSummaryDownloadsSelector,
  visitSummaryListPollingSelector
} from 'store/visitSummary/selectors';
import { fhirCurrentUserPatientId } from 'store/authentication/selectors';
import FileServices from 'services/FileServices';
import Config from 'react-native-config';
import {
  Selection,
  ShareVisitSummaryCcd,
  VisitSummaryDetails,
  VisitSummaryPollingEmr
} from 'store/visitSummary/types';
import dayjs from 'dayjs';
import { oc } from 'ts-optchain';
import { messageReply, messageShare } from 'store/messaging/actions';
import { TransmitSources } from 'store/transmit';
import {
  Action,
  AdapterSource,
  DFDDetailsReq,
  DFDHeadersObject,
  DispatcherAuthPrefix,
  DispatcherCategoryService,
  DispatcherEndpoint,
  DispatcherResource,
  MhpDetails
} from 'modules/types/common';
import wait from 'modules/utils/wait';
import { getDispatcherUrl } from 'modules/utils/UrlUtils';
import { getDownloadHeadersWithCurrentAccountConsumerId } from 'lib/downloads/utils';
import { getDocumentDecorator } from 'store/utils';

export const TIMELINE_STATUS_UPDATING = 'updating';
export const TIMELINE_STATUS_HEADER = 'x-timeline-status';
export const SET_VISIT_SUMMARY_FILTER = 'VISIT.SET_VISIT_SUMMARY_FILTER';
export const SET_VISIT_SUMMARY_SORT_BY = 'VISIT.SET_VISIT_SUMMARY_SORT_BY';
export const RESET_VISIT_SUMMARY_FILTER = 'VISIT.RESET_VISIT_SUMMARY_FILTER';

export const START_POLLING = 'VISIT.START_POLLING';
export const STOP_POLLING = 'VISIT.STOP_POLLING';

export const GET_VISIT_SUMMARY_CACHE_SUCCESS = 'VISIT_SUMMARY.GET_VISIT_SUMMARY_CACHE_SUCCESS';

export const GET_VISIT_SUMMARY_FULL_LIST = 'VISIT.GET_VISIT_SUMMARY_FULL_LIST';
export const GET_VISIT_SUMMARY_FULL_LIST_SUCCESS = 'VISIT.GET_VISIT_SUMMARY_FULL_LIST_SUCCESS';
export const GET_VISIT_SUMMARY_FULL_LIST_FAIL = 'VISIT.GET_VISIT_SUMMARY_FULL_LIST_FAIL';

export const GET_VISIT_SUMMARY_SHORT_LIST = 'VISIT.GET_VISIT_SUMMARY_SHORT_LIST';
export const GET_VISIT_SUMMARY_SHORT_LIST_SUCCESS = 'VISIT.GET_VISIT_SUMMARY_SHORT_LIST_SUCCESS';
export const GET_VISIT_SUMMARY_SHORT_LIST_FAIL = 'VISIT.GET_VISIT_SUMMARY_SHORT_LIST_FAIL';

export const GET_VISIT_SUMMARY_POLLING_STATUS = 'VISIT.GET_VISIT_SUMMARY_POLLING_STATUS';
export const GET_VISIT_SUMMARY_POLLING_STATUS_SUCCESS =
  'VISIT.GET_VISIT_SUMMARY_POLLING_STATUS_SUCCESS';
export const GET_VISIT_SUMMARY_POLLING_STATUS_FAIL = 'VISIT.GET_VISIT_SUMMARY_POLLING_STATUS_FAIL';

export const GET_VISIT_SUMMARY_DETAILS = 'VISIT.GET_VISIT_SUMMARY_DETAILS';
export const GET_VISIT_SUMMARY_DETAILS_SUCCESS = 'VISIT.GET_VISIT_SUMMARY_DETAILS_SUCCESS';
export const GET_VISIT_SUMMARY_DETAILS_FAIL = 'VISIT.GET_VISIT_SUMMARY_DETAILS_FAIL';

export const getVisitSummaryDetails = (
  source: string,
  id: string,
  dateOfEncounter: string,
  mhpDetails: MhpDetails | undefined = undefined
) => {
  if (!Config.VISIT_SUMMARY_EXPANSION_ENABLED && source === 'millennium') {
    source = 'fhir';
  }

  const req: DFDDetailsReq = {
    method: 'get',
    url: `/visit-summaries/${source}/${id}?dateOfEncounter=${dateOfEncounter}`
  };
  let client = 'visitSummary';

  if (!!Config.VISIT_SUMMARY_EXPANSION_ENABLED) {
    client = 'dispatcher';
    if (isEmpty(mhpDetails)) {
      // This shouldn't happen once we have everything moved over to expansion.
      //   There is at least one known way to reach this currently: updates (events) -> visit summary
      // TODO: after events have been made expansion-aware this if(true) block should be ok to remove.
      if (source === 'fhir') {
        source = 'millennium';
      }

      req.url = getDispatcherUrl(
        DispatcherCategoryService.HEALTH_RECORDS,
        DispatcherAuthPrefix.AUTHENTICATED,
        DispatcherResource.ENCOUNTERS_ID,
        DispatcherResource.VISIT_SUMMARIES
      );
      req.headers = {
        [DFDHeaders.mhpFilterEncounterId]: id,
        [DFDHeaders.filterSource]: source.toUpperCase(),
        [DFDHeaders.mhpAdapter]: AdapterSource.UTAH
      };
    } else {
      req.url = mhpDetails?.url || '';
      if (!isEmpty(mhpDetails?.headers)) {
        req.headers = {
          ...mhpDetails?.headers
        };
      }
    }
  }

  return {
    type: GET_VISIT_SUMMARY_DETAILS,
    payload: {
      client,
      request: req
    }
  };
};

export const RESET_VISIT_SUMMARY_DETAILS = 'VISIT.RESET_VISIT_SUMMARY_DETAILS';
export const resetVisitSummaryDetails = () => ({ type: RESET_VISIT_SUMMARY_DETAILS });

export const GET_VISIT_SUMMARY_DOCUMENT = 'VISIT.GET_VISIT_SUMMARY_DOCUMENT';
export const GET_VISIT_SUMMARY_DOCUMENT_SUCCESS = 'VISIT.GET_VISIT_SUMMARY_DOCUMENT_SUCCESS';
export const GET_VISIT_SUMMARY_DOCUMENT_FAIL = 'VISIT.GET_VISIT_SUMMARY_DOCUMENT_FAIL';

export const getVisitSummaryBinaryDocument = (acceptType: string, documentId: string) =>
  getDocumentDecorator(GET_VISIT_SUMMARY_DOCUMENT)(documentId, acceptType);

export const GET_VISIT_SUMMARY_CCD = 'VISIT.GET_VISIT_SUMMARY_CCD';
export const GET_VISIT_SUMMARY_CCD_SUCCESS = 'VISIT.GET_VISIT_SUMMARY_CCD_SUCCESS';
export const GET_VISIT_SUMMARY_CCD_FAIL = 'VISIT.GET_VISIT_SUMMARY_CCD_FAIL';

export const getVisitSummaryCcd = (encounterId: string) => async (
  dispatch: Function,
  getState: () => RootState
) => {
  const state = getState();
  let url = `${Config.CCD_BASE_URL}/download`;
  if (!!Config.CCD_EXPANSION_BASE_URL) {
    url = `${Config.CCD_EXPANSION_BASE_URL}/_download`;
  }

  // Check for in flight downloads if there are any then return
  if (visitSummaryDownloadsSelector(state)[encounterId] === 'pending') {
    return;
  }

  dispatch({ type: GET_VISIT_SUMMARY_CCD, payload: { id: encounterId } });

  const auditData = base64.encode(
    JSON.stringify({
      categoryId: '1',
      message: 'Visit Summary'
    })
  );
  const headers = {
    ...getDownloadHeadersWithCurrentAccountConsumerId(state, url)
  };
  if (!!Config.CCD_EXPANSION_BASE_URL) {
    headers[DFDHeaders.mhpFilterEncounterId] = encounterId;
    headers[DFDHeaders.mhpAuditData] = auditData;
  } else {
    headers[DFDHeaders.encounterId] = encounterId;
    headers[DFDHeaders.auditData] = auditData;
  }

  try {
    const response = await FileServices.download(
      `visit-summary-ccd-${encounterId}.zip`,
      url,
      headers
    );

    dispatch({ type: GET_VISIT_SUMMARY_CCD_SUCCESS, payload: { id: encounterId, response } });
  } catch (err) {
    dispatch({ type: GET_VISIT_SUMMARY_CCD_FAIL, payload: { id: encounterId }, error: err });
  }
};

export const GET_MULTIPLE_VISIT_SUMMARY_CCD = 'VISIT.GET_MULTIPLE_VISIT_SUMMARY_CCD';
export const GET_MULTIPLE_VISIT_SUMMARY_CCD_SUCCESS =
  'VISIT.GET_MULTIPLE_VISIT_SUMMARY_CCD_SUCCESS';
export const GET_MULTIPLE_VISIT_SUMMARY_CCD_FAIL = 'VISIT.GET_MULTIPLE_VISIT_SUMMARY_CCD_FAIL';

export const getMultipleVisitSummaryCcd = (
  encounterIdList: string[] = [],
  downloadAllFlag?: boolean
) => async (dispatch: Function, getState: () => RootState) => {
  const state = getState();
  const patientId = fhirCurrentUserPatientId(state);

  // Check for in flight multiple downloads if there are any then return
  if (visitSummaryDownloadsSelector(state)[patientId] === 'pending') return;
  const concatenatedEncounterIdList = encounterIdList.join();
  // If there's no Ids then return
  if (!concatenatedEncounterIdList) return;

  dispatch({ type: GET_MULTIPLE_VISIT_SUMMARY_CCD, payload: { patientId } });

  let url;
  const currentAccountHeaders = getDownloadHeadersWithCurrentAccountConsumerId(state);
  const headers = {
    ...currentAccountHeaders,
    'x-audit-data': base64.encode(
      JSON.stringify({
        categoryId: '1',
        message: 'Visit Summary'
      })
    )
  };
  if (!!Config.CCD_EXPANSION_BASE_URL) {
    url = `${Config.CCD_EXPANSION_BASE_URL}/_download${
      downloadAllFlag
        ? '?all=true&templates=AMBULATORY_SUMMARY_VDT,INPATIENT_SUMMARY_VDT,TRANSITION_OF_CARE'
        : ''
    }`;
    headers['mhp-filter-encounter-id'] = concatenatedEncounterIdList;
  } else {
    url = `${Config.CCD_BASE_URL}/download${
      downloadAllFlag
        ? '?all=true&templates=AMBULATORY_SUMMARY_VDT,INPATIENT_SUMMARY_VDT,TRANSITION_OF_CARE'
        : ''
    }`;
  }

  if (!downloadAllFlag) {
    headers['x-encounter-id'] = concatenatedEncounterIdList;
  }

  try {
    const response = await FileServices.download(
      `visit-summary-ccd-${patientId}.zip`,
      url,
      headers
    );

    dispatch({
      type: GET_MULTIPLE_VISIT_SUMMARY_CCD_SUCCESS,
      payload: { patientId, response }
    });
  } catch (err) {
    dispatch({
      type: GET_MULTIPLE_VISIT_SUMMARY_CCD_FAIL,
      payload: { patientId },
      error: err
    });
  }
};

/**
 * Share Visit Summary CCD
 */
export const SHARE_VISIT_SUMMARY_CCD = 'VISIT.SHARE_VISIT_SUMMARY_CCD';
export const SHARE_VISIT_SUMMARY_CCD_SUCCESS = 'VISIT.SHARE_VISIT_SUMMARY_CCD_SUCCESS';
export const SHARE_VISIT_SUMMARY_CCD_FAIL = 'VISIT.SHARE_VISIT_SUMMARY_CCD_FAIL';

export const shareVisitSummaryCcd = (
  encounterId: string,
  toAddress: string,
  message: string,
  useSecureEmail: boolean,
  contactPhoneNumber: string
): Thunk => (dispatch: Function, getState: () => RootState): Action => {
  const auditData = base64.encode(
    JSON.stringify({
      categoryId: '1',
      message: 'Visit Summary'
    })
  );
  const data: ShareVisitSummaryCcd = {
    contactPhoneNumber,
    message,
    toAddress,
    useSecureEmail
  };

  let url = '/sharedocs';
  let headers: DFDHeadersObject = {
    [DFDHeaders.encounterId]: encounterId,
    [DFDHeaders.auditData]: auditData
  };
  if (!!Config.CCD_EXPANSION_BASE_URL) {
    url = '/_transmit';
    headers = {
      [DFDHeaders.mhpFilterEncounterId]: encounterId,
      [DFDHeaders.mhpAuditData]: auditData
    };
    data.subject = '';
  }

  return dispatch({
    type: SHARE_VISIT_SUMMARY_CCD,
    payload: {
      client: 'expansionCCD',
      request: {
        method: 'post',
        url,
        headers,
        data
      }
    }
  });
};

/**
 * Share Multiple Visit Summary CCD
 */
export const SHARE_MULTIPLE_VISIT_SUMMARY_CCD = 'VISIT.SHARE_MULTIPLE_VISIT_SUMMARY_CCD';
export const SHARE_MULTIPLE_VISIT_SUMMARY_CCD_SUCCESS =
  'VISIT.SHARE_MULTIPLE_VISIT_SUMMARY_CCD_SUCCESS';
export const SHARE_MULTIPLE_VISIT_SUMMARY_CCD_FAIL = 'VISIT.SHARE_MULTIPLE_VISIT_SUMMARY_CCD_FAIL';

export const shareMultipleVisitSummaryCcd = (
  encounterIdList: number[],
  toAddress: string,
  message: string,
  useSecureEmail: boolean,
  contactPhoneNumber: string
): Thunk => (dispatch: Function, getState: () => RootState): Action => {
  const auditData = base64.encode(
    JSON.stringify({
      categoryId: '1',
      message: 'Visit Summary'
    })
  );
  const data: ShareVisitSummaryCcd = {
    contactPhoneNumber,
    encounterIdList,
    message,
    toAddress,
    useSecureEmail
  };

  let url = '/sharedocs';
  let headers: DFDHeadersObject = {
    [DFDHeaders.auditData]: auditData
  };
  if (!!Config.CCD_EXPANSION_BASE_URL) {
    url = '/_transmit';
    headers = {
      [DFDHeaders.mhpAuditData]: auditData,
      [DFDHeaders.mhpFilterEncounterId]: encounterIdList.join(',')
    };
    data.subject = '';
    delete data.encounterIdList;
  }
  return dispatch({
    type: SHARE_MULTIPLE_VISIT_SUMMARY_CCD,
    payload: {
      client: 'expansionCCD',
      request: {
        method: 'post',
        url,
        headers,
        data
      }
    }
  });
};

/**
 * Message Reply
 */

export const createReplyBodyFromVisitSummary = (visitSummary: VisitSummaryDetails) => {
  const visitDate = dayjs(visitSummary.date).format('MM/DD/YYYY');
  const encounterNumber = visitSummary.fin;
  const youCameFor = oc(visitSummary).chiefComplaint.valueString('');
  const whatHappenedList = oc(visitSummary).procedures([]);
  const whatHappenedText = whatHappenedList.map(procedure => procedure.name).join(',');
  const replyBody = `Date: ${visitDate}\n\nEncounter Number: ${encounterNumber}\n\nYou Came For: ${youCameFor}\n\nWhat Happened: ${whatHappenedText}`;

  return replyBody;
};

export const replyToVisitSummary = (
  visitSummary: VisitSummaryDetails,
  isShare = false,
  returnBackTo?: string
): Thunk => dispatch => {
  // Create Potential Message Body
  if (!visitSummary) {
    return;
  }
  const params = {
    subject: 'Visit Summary Question',
    readonly: createReplyBodyFromVisitSummary(visitSummary)
  };

  if (isShare) {
    dispatch(
      messageShare({
        ...params,
        transmitParams: {
          source: TransmitSources.VisitSummary,
          payload: visitSummary.id,
          returnBackTo
        }
      })
    );
  } else {
    dispatch(
      messageReply(
        {
          ...params,
          transmitParams: {
            source: TransmitSources.VisitSummary,
            payload: visitSummary.id,
            returnBackTo
          }
        },
        oc(visitSummary).primaryProvider.providerId('')
      )
    );
  }
};

const getPollingStatus = () => {
  const urlBase = DispatcherResource.VISIT_SUMMARIES;
  let url = `${urlBase}/pollingStatusUpdate`;
  let client = 'visitSummary';
  if (!!Config.VISIT_SUMMARY_EXPANSION_ENABLED) {
    client = 'dispatcher';
    url = getDispatcherUrl(
      DispatcherCategoryService.HEALTH_RECORDS,
      DispatcherAuthPrefix.AUTHENTICATED,
      DispatcherResource.VISIT_SUMMARIES,
      DispatcherEndpoint.POLLING_STATUS
    );
  }
  return {
    type: GET_VISIT_SUMMARY_POLLING_STATUS,
    payload: {
      client,
      request: {
        method: 'get',
        url
      }
    }
  };
};

export const isTimelineUpdating = (action: AnyAction | null) => {
  if (isEmpty(action?.payload)) {
    return false;
  }

  if (
    action?.payload?.headers?.[TIMELINE_STATUS_HEADER] === TIMELINE_STATUS_UPDATING ||
    action?.payload?.data?.status === TIMELINE_STATUS_UPDATING ||
    (Array.isArray(action?.payload?.data) &&
      !!(action?.payload?.data || []).find(
        (emr: VisitSummaryPollingEmr) => emr?.status === TIMELINE_STATUS_UPDATING
      ))
  ) {
    return true;
  }

  return false;
};

export const getPollingStatusLooped = (postAction: Function) => async (
  dispatch: Dispatch,
  getState: () => RootState
) => {
  const result = await dispatch(getPollingStatus());

  // Don't poll if error
  if (result.error) {
    return;
  }

  // If we're already polling or the status has updated don't start another polling loop
  if (!isTimelineUpdating(result) || visitSummaryListPollingSelector(getState())) {
    return;
  }

  // Start polling
  dispatch({ type: START_POLLING });
  let attempts = 0;
  const attemptsLimit = 15;
  /* eslint-disable no-await-in-loop */
  while (true) {
    // wait 10 seconds
    await wait(10000);

    const pollingResult = await dispatch(getPollingStatus());

    if (pollingResult.error) {
      break;
    }

    if (!isTimelineUpdating(pollingResult)) {
      break;
    }

    attempts += 1;
    if (attempts === attemptsLimit) {
      break;
    }
  }

  // stop polling
  dispatch({ type: STOP_POLLING });
  dispatch(postAction());
};

export const SET_SELECTED = 'VISIT.SET_SELECTED';
export const SET_SELECTED_BY_ID = 'VISIT.SET_SELECTED_BY_ID';
export const RESET_SELECTED = 'VISIT.RESET_SELECTED';
export const SET_IS_SELECTING = 'VISIT.SET_IS_SELECTING';

export const setSelected = (selected: Selection) => ({
  type: SET_SELECTED,
  payload: selected
});

export const setSelectedById = (selected: number) => ({
  type: SET_SELECTED_BY_ID,
  payload: selected
});

export const setIsSelecting = (isSelecting: boolean) => ({
  type: SET_IS_SELECTING,
  payload: isSelecting
});

export const resetSelected = () => ({ type: RESET_SELECTED });
